import PropTypes from 'prop-types';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import styled from 'styled-components';
import UIPopoverBody from 'UIComponents/tooltip/UIPopoverBody';
import { EngagementOrInteractionTypePropType } from 'customer-data-objects/engagement/EngagementOrInteractionTypePropType';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import UniversalAssociationsSelectError from './UniversalAssociationsSelectError';
import UniversalAssociationsSelectSearchPane, { SearchPaneSkeleton } from './UniversalAssociationsSelectSearchPane';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module '../r... Remove this comment to see the full error message
import UniversalAssociationRecord from '../records/UniversalAssociationRecord';
import ImmutablePropTypes from 'react-immutable-proptypes';
import UISelect from 'UIComponents/input/UISelect';
import { getRefreshUrl } from '../helpers/getRefreshUrl';
import UIScreenReaderOnly from 'UIComponents/text/UIScreenReaderOnly';
import uniqueId from 'transmute/uniqueId';
import UITruncateString from 'UIComponents/text/UITruncateString';
import ReferenceLiteName from 'reference-resolvers-lite/components/ReferenceLiteName';
import { CRM_OBJECT } from 'customer-data-objects/constants/CrmObjectType';
import UniversalAssociationsSelectSideNav from './UniversalAssociationsSelectSideNav';
import { UniversalAssociationsSelectSideNavSkeleton } from './UniversalAssociationsSelectSideNavSkeleton';
import { ShinySkeletonText } from '../helpers/ShinySkeletonText';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_HEIGHT = 260;
const POPOVER_WITH_SIDENAV_WIDTH = 480;
const POPOVER_WITHOUT_SIDENAV_WIDTH = 320;
const LG_POPOVER_HEIGHT = 460;
const LG_POPOVER_WITH_SIDENAV_WIDTH = 680;
const LG_POPOVER_WITHOUT_SIDENAV_WIDTH = 520;
const StyledPopoverBody = styled(UIPopoverBody).withConfig({
  displayName: "UniversalAssociationsSelect__StyledPopoverBody",
  componentId: "rtk58q-0"
})(["height:", "px;"], POPOVER_HEIGHT);
const LgStyledPopoverBody = styled(UIPopoverBody).withConfig({
  displayName: "UniversalAssociationsSelect__LgStyledPopoverBody",
  componentId: "rtk58q-1"
})(["height:", "px;"], LG_POPOVER_HEIGHT);
const associationCountReducer = (acc, association) => {
  const isSelectedCount = association.get('associationOptionRecords').filter(associationOptionRecord => associationOptionRecord.get('isSelected')).size;
  return acc + isSelectedCount;
};
function UniversalAssociationsSelect({
  'aria-labelledby': ariaLabelledBy,
  associationRecords,
  betAssociationsCount,
  betSelect,
  buttonUse,
  canEdit,
  canView,
  currentUserCanCommunicateWithEngagement,
  descriptionLabelKey,
  emailRecipientsIds,
  engagementOrInteractionType,
  isError,
  isLoading,
  labelKeyWhenNotEditable,
  onFocus,
  onSelectAssociation,
  onUnselectAssociation,
  placement,
  popoverLabelKey,
  preselectedObjectTypeId,
  readOnly,
  shouldDisableLastAssociation,
  singleObjectSelectObjectTypeId,
  skeletonStateWidth,
  size,
  summaryAssociations,
  summaryAssociationsCount
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldShowPopoverOpenMessage, setShouldShowPopoverOpenMessage] = useState(false);
  const [selectedObjectTypeId, setSelectedObjectTypeId] = useState(preselectedObjectTypeId || singleObjectSelectObjectTypeId);
  const selectUniqueId = useRef('');
  const shouldShowSideNav = !singleObjectSelectObjectTypeId;
  useEffect(() => {
    selectUniqueId.current = uniqueId('universal-associations-select-dropdown-');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleToggleIsOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const handleSetSelectedObjectTypeId = useCallback(objectType => {
    setSelectedObjectTypeId(objectType);
  }, [setSelectedObjectTypeId]);
  const allAssociationsCount = useMemo(() => {
    if (isLoading || isError) {
      return 0;
    }
    if (associationRecords.size) {
      return associationRecords.reduce(associationCountReducer, 0);
    }
    return summaryAssociationsCount || 0;
  }, [associationRecords, isLoading, isError, summaryAssociationsCount]);
  const singleObjectTypeIdAssociationCount = useMemo(() => {
    if (isLoading || isError || !singleObjectSelectObjectTypeId) {
      return 0;
    }
    if (associationRecords.size) {
      const filteredAssociations = associationRecords.filter(record => record.get('toObjectTypeId') === singleObjectSelectObjectTypeId);
      return filteredAssociations.reduce(associationCountReducer, 0);
    }
    const summarySingleObjectCount = summaryAssociations && summaryAssociations[singleObjectSelectObjectTypeId] && summaryAssociations[singleObjectSelectObjectTypeId].length;
    return summarySingleObjectCount || 0;
  }, [isLoading, isError, singleObjectSelectObjectTypeId, associationRecords, summaryAssociations]);
  const handleToggleSelect = useCallback((associationRecord, associationOptionRecord) => {
    if (associationOptionRecord.get('isSelected')) {
      return onUnselectAssociation(associationRecord, associationOptionRecord);
    }
    return onSelectAssociation(associationRecord, associationOptionRecord);
  }, [onSelectAssociation, onUnselectAssociation]);
  const getShouldDisableLastAssociation = useCallback(() => {
    if (!shouldDisableLastAssociation) {
      return false;
    }
    if (!shouldShowSideNav) {
      return singleObjectTypeIdAssociationCount === 1 && allAssociationsCount === 1;
    }
    return allAssociationsCount === 1;
  }, [allAssociationsCount, singleObjectTypeIdAssociationCount, shouldDisableLastAssociation, shouldShowSideNav]);
  const PopoverContent = useCallback(() => {
    const PopoverBody = size === 'large' ? LgStyledPopoverBody : StyledPopoverBody;
    const renderedPopoverItems = associationRecords.size ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [shouldShowSideNav && /*#__PURE__*/_jsx(UniversalAssociationsSelectSideNav, {
        associationRecords: associationRecords,
        allAssociationsCount: allAssociationsCount,
        selectedObjectTypeId: selectedObjectTypeId,
        onSetSelectedObjectTypeId: handleSetSelectedObjectTypeId
      }), /*#__PURE__*/_jsx(UniversalAssociationsSelectSearchPane, {
        associationRecords: associationRecords,
        canEdit: canEdit,
        emailRecipientsIds: emailRecipientsIds,
        engagementOrInteractionType: engagementOrInteractionType,
        currentUserCanCommunicateWithEngagement: currentUserCanCommunicateWithEngagement,
        onToggleSelectAssociation: handleToggleSelect,
        readOnly: readOnly,
        selectedObjectTypeId: selectedObjectTypeId,
        shouldDisableLastAssociation: getShouldDisableLastAssociation(),
        shouldAddSearchBackground: !shouldShowSideNav,
        shouldShowPopoverOpenMessage: shouldShowPopoverOpenMessage,
        setShouldShowPopoverOpenMessage: setShouldShowPopoverOpenMessage
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [shouldShowSideNav && /*#__PURE__*/_jsx(UniversalAssociationsSelectSideNavSkeleton, {}), /*#__PURE__*/_jsx(SearchPaneSkeleton, {
        isSingleObjectType: !shouldShowSideNav
      })]
    });
    return /*#__PURE__*/_jsxs(ErrorBoundary, {
      boundaryName: "AssociationsSelectError",
      ErrorComponent: UniversalAssociationsSelectError,
      children: [/*#__PURE__*/_jsx(PopoverBody, {
        className: "p-all-0 flex-row",
        children: renderedPopoverItems
      }), betSelect]
    });
  }, [shouldShowSideNav, associationRecords, allAssociationsCount, handleSetSelectedObjectTypeId, selectedObjectTypeId, canEdit, emailRecipientsIds, engagementOrInteractionType, currentUserCanCommunicateWithEngagement, handleToggleSelect, readOnly, getShouldDisableLastAssociation, betSelect, size, shouldShowPopoverOpenMessage]);
  const objectPrimaryDisplayLabel = useMemo(() => {
    // singleObjectTypeIdAssociationCount is only calculated when singleObjectSelectObjectTypeId has a value
    if (singleObjectTypeIdAssociationCount !== 1) {
      return null;
    }
    if (associationRecords.size) {
      const object = associationRecords.getIn([selectedObjectTypeId, 'associationOptionRecords']).find(record => record.get('isSelected'));
      return object.get('primaryDisplayLabel');
    }
    const summaryObjectId = summaryAssociations && selectedObjectTypeId && summaryAssociations[selectedObjectTypeId] && summaryAssociations[selectedObjectTypeId][0].id;
    return summaryObjectId ? /*#__PURE__*/_jsx(ReferenceLiteName, {
      id: `${summaryObjectId}`,
      objectTypeId: selectedObjectTypeId,
      referenceType: CRM_OBJECT
    }) : null;
  }, [associationRecords, selectedObjectTypeId, singleObjectTypeIdAssociationCount, summaryAssociations]);
  const shouldDisableErrorToolTip = canView && !isError;
  const PopoverLabel = useMemo(() => {
    if (!canView) {
      // Currently users with ownedOnly permissions cannot view associations of an engagement they do not own through
      // GraphQL. Rather than show an error, we render a placeholder that is disabled. However, currently this is not supported
      // in permissions. https://git.hubteam.com/HubSpot/CRM-Issues/issues/7309
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: labelKeyWhenNotEditable
      });
    }
    if (isError) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "universalAssociationsSelect.dataFetchError.button"
      });
    }
    const count = shouldShowSideNav ? allAssociationsCount + betAssociationsCount : singleObjectTypeIdAssociationCount;
    return /*#__PURE__*/_jsx(UITruncateString, {
      "data-test-id": 'universal-associations-select-button-placeholder',
      maxWidth: 200,
      useFlex: true,
      children: objectPrimaryDisplayLabel || /*#__PURE__*/_jsx(FormattedMessage, {
        message: popoverLabelKey,
        options: {
          count
        }
      })
    });
  }, [canView, isError, shouldShowSideNav, allAssociationsCount, betAssociationsCount, singleObjectTypeIdAssociationCount, objectPrimaryDisplayLabel, popoverLabelKey, labelKeyWhenNotEditable]);
  const TooltipText = useMemo(() => !canView ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "universalAssociationsSelect.dataFetchError.tooltipPermissionErrorV2"
  }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "universalAssociationsSelect.dataFetchError.tooltip_jsx",
    options: {
      href: getRefreshUrl()
    },
    elements: {
      Link: UILink
    }
  }), [canView]);
  const headingText = useMemo(() => !canView ? undefined : I18n.text('universalAssociationsSelect.dataFetchError.tooltipHeading'), [canView]);
  const width = useMemo(() => {
    if (size === 'large') {
      return shouldShowSideNav ? LG_POPOVER_WITH_SIDENAV_WIDTH : LG_POPOVER_WITHOUT_SIDENAV_WIDTH;
    }
    return shouldShowSideNav ? POPOVER_WITH_SIDENAV_WIDTH : POPOVER_WITHOUT_SIDENAV_WIDTH;
  }, [shouldShowSideNav, size]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(ShinySkeletonText, {
      "data-test-id": "universal-associations-select-loading",
      className: "p-top-1 p-x-0 p-bottom-1",
      width: skeletonStateWidth
    });
  }
  const descriptionUniqueId = uniqueId('universal-associations-select-button-');
  return /*#__PURE__*/_jsx(UIPopover, {
    "data-selenium-test": "universal-associations-select-popover",
    renderContent: PopoverContent,
    closeOnOutsideClick: true,
    closeOnTargetLeave: true,
    onOpenChange: handleToggleIsOpen,
    open: isOpen,
    onOpenStart: () => setShouldShowPopoverOpenMessage(true)
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    ,
    placement: placement,
    width: width,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        disabled: shouldDisableErrorToolTip,
        headingText: headingText,
        title: TooltipText,
        children: /*#__PURE__*/_jsx(UISelect, {
          "aria-describedby": descriptionUniqueId,
          "aria-labelledby": ariaLabelledBy
          // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
          ,
          onClick: handleToggleIsOpen,
          onFocus: onFocus,
          onMouseEnter: onFocus,
          open: false,
          placeholder: PopoverLabel,
          buttonUse: buttonUse,
          "aria-pressed": isOpen,
          "data-test-id": "universal-associations-select-button",
          "data-selenium-test": "universal-associations-select-button",
          disabled: !shouldDisableErrorToolTip,
          id: selectUniqueId.current
        })
      }), /*#__PURE__*/_jsx(UIScreenReaderOnly, {
        children: /*#__PURE__*/_jsx("div", {
          id: descriptionUniqueId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: descriptionLabelKey || 'universalAssociationsSelect.accessibility.button'
          })
        })
      })]
    })
  });
}
UniversalAssociationsSelect.defaultProps = {
  betAssociationsCount: 0,
  betSelect: null,
  buttonUse: 'link',
  canEdit: false,
  canView: true,
  isError: false,
  isLoading: false,
  labelKeyWhenNotEditable: 'universalAssociationsSelect.selectedAssociationsLabel',
  placement: 'bottom right',
  popoverLabelKey: 'universalAssociationsSelect.button',
  shouldDisableLastAssociation: true,
  skeletonStateWidth: 175,
  size: 'small'
};
UniversalAssociationsSelect.propTypes = {
  'aria-labelledby': PropTypes.string,
  associationRecords: ImmutablePropTypes.orderedMapOf(UniversalAssociationRecord),
  betAssociationsCount: PropTypes.number,
  betSelect: PropTypes.node,
  buttonUse: UIButton.propTypes.use,
  canEdit: PropTypes.bool,
  canView: PropTypes.bool,
  currentUserCanCommunicateWithEngagement: PropTypes.bool,
  descriptionLabelKey: PropTypes.string,
  emailRecipientsIds: PropTypes.array,
  engagementOrInteractionType: EngagementOrInteractionTypePropType.isRequired,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  labelKeyWhenNotEditable: PropTypes.string,
  onFocus: PropTypes.func,
  onSelectAssociation: PropTypes.func.isRequired,
  onUnselectAssociation: PropTypes.func.isRequired,
  placement: PropTypes.string,
  popoverLabelKey: PropTypes.string,
  preselectedObjectTypeId: PropTypes.string,
  readOnly: PropTypes.bool,
  shouldDisableLastAssociation: PropTypes.bool,
  singleObjectSelectObjectTypeId: PropTypes.string,
  skeletonStateWidth: PropTypes.number,
  size: PropTypes.string,
  summaryAssociations: PropTypes.object,
  summaryAssociationsCount: PropTypes.number
};
export default UniversalAssociationsSelect;